import { useEffect, useState } from "react";
import { Save, Upload, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layout/AdminLayout";

function fullUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

// Kept outside the render fn so inputs don't lose focus on every keystroke.
function Field({ label, value, onChange, name, type = "text", rows, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>{label}</label>
      {rows ? (
        <textarea rows={rows} className="brand-input font-mono text-sm" value={value || ""} onChange={(e) => onChange(name, e.target.value)} data-testid={`settings-${name}`} />
      ) : (
        <input type={type} className="brand-input" value={value || ""} onChange={(e) => onChange(name, e.target.value)} data-testid={`settings-${name}`} />
      )}
      {hint && <p className="text-xs mt-1.5" style={{ color: "var(--text-secondary)" }}>{hint}</p>}
    </div>
  );
}

function ImageUpload({ label, value, name, onChange, onUpload }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>{label}</label>
      <div className="flex gap-3 items-center flex-wrap">
        {value && <img src={value} alt="" className="w-20 h-20 rounded-xl object-cover border" style={{ borderColor: "var(--border-light)" }} />}
        <label className="btn-secondary cursor-pointer" style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }} data-testid={`upload-${name}`}>
          <Upload size={14} /> Enviar arquivo
          <input type="file" accept="image/*" hidden onChange={(e) => onUpload(name, e)} />
        </label>
        <input className="brand-input flex-1 min-w-[220px]" placeholder="Ou cole URL" value={value || ""} onChange={(e) => onChange(name, e.target.value)} />
      </div>
    </div>
  );
}

function Section({ id, title, subtitle, isOpen, onToggle, children }) {
  return (
    <section className="brand-card overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full text-left px-8 py-6 flex items-center justify-between hover:bg-[color:var(--bg)] transition-colors"
        data-testid={`section-toggle-${id}`}
      >
        <div>
          <h2 className="font-serif text-xl md:text-2xl" style={{ color: "var(--text-primary)" }}>{title}</h2>
          {subtitle && <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{subtitle}</p>}
        </div>
        <ChevronDown
          size={20}
          style={{ color: "var(--copper)", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.25s" }}
        />
      </button>
      {isOpen && (
        <div className="px-8 pb-8 pt-2 space-y-4 border-t" style={{ borderColor: "var(--border-light)" }}>
          {children}
        </div>
      )}
    </section>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState({});
  const [openSection, setOpenSection] = useState("marca");

  useEffect(() => {
    document.title = "Editor da plataforma — Admin";
    api.get("/settings").then((r) => setS(r.data || {}));
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/settings", s);
      toast.success("Conteúdo salvo com sucesso");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setField = (key, value) => setS((prev) => ({ ...prev, [key]: value }));

  const handleUpload = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((prev) => ({ ...prev, [key]: fullUrl(r.data.url) }));
      toast.success("Imagem carregada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = (id) => setOpenSection((prev) => (prev === id ? null : id));

  return (
    <AdminLayout>
      <div className="p-8 lg:p-12 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-10">
          <div>
            <div className="eyebrow mb-2">Editor da plataforma</div>
            <h1 className="font-serif text-4xl" style={{ color: "var(--text-primary)" }}>Edite toda a página inicial</h1>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
              Clique em cada seção para expandir. Salve quando terminar — as mudanças aparecem instantaneamente no site público.
            </p>
          </div>
          <button onClick={save} className="btn-primary self-start sm:self-end" data-testid="save-settings-btn">
            <Save size={16} /> Salvar tudo
          </button>
        </div>

        <div className="space-y-4">
          <Section id="marca" title="1 · Marca & Contato" subtitle="Logo, redes sociais, e-mail" isOpen={openSection === "marca"} onToggle={toggle}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome da marca" name="brand_name" value={s.brand_name} onChange={setField} />
              <ImageUpload label="Logo" name="logo_url" value={s.logo_url} onChange={setField} onUpload={handleUpload} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Instagram (URL)" name="instagram" value={s.instagram} onChange={setField} />
              <Field label="WhatsApp (com DDI)" name="whatsapp" value={s.whatsapp} onChange={setField} />
              <Field label="E-mail" name="email" value={s.email} onChange={setField} />
            </div>
          </Section>

          <Section id="hero" title="2 · Hero (topo da Home)" subtitle="Título principal, subtítulo, foto e tagline" isOpen={openSection === "hero"} onToggle={toggle}>
            <Field label="Título principal (headline)" name="headline" value={s.headline} onChange={setField} rows={2} />
            <Field label="Subtítulo (subheadline)" name="subheadline" value={s.subheadline} onChange={setField} rows={3} />
            <Field label="Tagline em italic (abaixo dos botões)" name="hero_tagline" value={s.hero_tagline} onChange={setField} rows={2} />
            <ImageUpload label="Foto principal (Hero)" name="photo_url" value={s.photo_url} onChange={setField} onUpload={handleUpload} />
          </Section>

          <Section id="destaque" title="3 · Frase de destaque (faixa dourada)" subtitle="Aparece na faixa dourada logo abaixo do hero" isOpen={openSection === "destaque"} onToggle={toggle}>
            <Field label="Frase de destaque" name="featured_phrase" value={s.featured_phrase} onChange={setField} rows={2} hint='A frase que aparece com o selo "Inédito no Brasil".' />
          </Section>

          <Section id="manifesto" title="4 · Manifesto" subtitle="Duas frases fortes + lista + caixa de fechamento" isOpen={openSection === "manifesto"} onToggle={toggle}>
            <Field label="1ª linha (regular)" name="manifesto_line1" value={s.manifesto_line1} onChange={setField} rows={2} />
            <Field label="2ª linha (italic dourada)" name="manifesto_line2" value={s.manifesto_line2} onChange={setField} rows={2} />
            <Field label="Prefixo da lista" name="manifesto_prefix" value={s.manifesto_prefix} onChange={setField} hint='Ex: "E isso muda completamente"' />
            <Field label="Itens da lista (uma linha = um item)" name="manifesto_items" value={s.manifesto_items} onChange={setField} rows={5} hint="Cada linha vira um item com bolinha dourada." />
            <Field label="Texto da caixa de fechamento" name="manifesto_box" value={s.manifesto_box} onChange={setField} rows={3} />
          </Section>

          <Section id="sobre" title="5 · Sobre — Quem é Ana Paula Moraes" subtitle="Apresentação, formação, parágrafos, citação" isOpen={openSection === "sobre"} onToggle={toggle}>
            <ImageUpload label="Foto na seção Sobre" name="about_photo_url" value={s.about_photo_url} onChange={setField} onUpload={handleUpload} />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Título da seção" name="about_title" value={s.about_title} onChange={setField} />
              <Field label="Formação (linha dourada)" name="about_role" value={s.about_role} onChange={setField} hint='Ex: "Economista, Advogada e Psicanalista."' />
            </div>
            <Field label="Parágrafo 1" name="about_p1" value={s.about_p1} onChange={setField} rows={3} />
            <Field label="Parágrafo 2" name="about_p2" value={s.about_p2} onChange={setField} rows={3} />
            <Field label="Parágrafo 3" name="about_p3" value={s.about_p3} onChange={setField} rows={3} />
            <Field label="Citação em destaque (dentro da caixa)" name="about_quote" value={s.about_quote} onChange={setField} rows={3} />
            <Field label="Frase de fechamento da seção Sobre" name="about_closing" value={s.about_closing} onChange={setField} rows={3} />
          </Section>

          <Section id="missao" title="6 · Missão & Visão" subtitle="Cards lado a lado" isOpen={openSection === "missao"} onToggle={toggle}>
            <Field label="Missão" name="mission" value={s.mission} onChange={setField} rows={4} />
            <Field label="Visão" name="vision" value={s.vision} onChange={setField} rows={4} />
          </Section>

          <Section id="valores" title="7 · Nossos Valores" subtitle="Grid de 6 cards (Título | Descrição por linha)" isOpen={openSection === "valores"} onToggle={toggle}>
            <Field label="Título da seção" name="values_title" value={s.values_title} onChange={setField} />
            <Field
              label="Cards de valores (6 linhas — formato: Título|Descrição)"
              name="values_items"
              value={s.values_items}
              onChange={setField}
              rows={9}
              hint="Cada linha = 1 card. Separe título e descrição com |. Ex: Feminilidade Consciente|Acreditamos em uma feminilidade madura…"
            />
          </Section>

          <Section id="metodo" title="8 · Método (Eu vou / Eu não vou te ensinar)" subtitle="Listas comparativas + público-alvo" isOpen={openSection === "metodo"} onToggle={toggle}>
            <Field label="Título da seção Método" name="method_title" value={s.method_title} onChange={setField} rows={2} />
            <Field label="EU NÃO VOU TE ENSINAR — uma linha = 1 item" name="method_wont_teach" value={s.method_wont_teach} onChange={setField} rows={7} />
            <Field label="EU VOU TE ENSINAR — uma linha = 1 item" name="method_will_teach" value={s.method_will_teach} onChange={setField} rows={7} />
            <Field label="ESSA MENTORIA É PARA VOCÊ SE… — uma linha = 1 item" name="method_for_you" value={s.method_for_you} onChange={setField} rows={6} />
            <Field label="ESSA MENTORIA NÃO É PARA VOCÊ SE… — uma linha = 1 item" name="method_not_for_you" value={s.method_not_for_you} onChange={setField} rows={6} />
            <Field label="Frase de fechamento (dourada)" name="method_closing" value={s.method_closing} onChange={setField} rows={3} />
          </Section>

          <Section id="cta_final" title="9 · CTA final" subtitle="Bloco de chamada antes do rodapé" isOpen={openSection === "cta_final"} onToggle={toggle}>
            <Field label="Título do CTA final" name="cta_final_title" value={s.cta_final_title} onChange={setField} rows={2} />
            <Field label="Descrição" name="cta_final_desc" value={s.cta_final_desc} onChange={setField} rows={3} />
            <Field label="Texto do botão CTA (legado)" name="cta_text" value={s.cta_text} onChange={setField} />
          </Section>
        </div>

        <div className="mt-10 flex justify-end">
          <button onClick={save} className="btn-primary" data-testid="save-settings-btn-bottom">
            <Save size={16} /> Salvar todas as mudanças
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
